/**
 * @file api.ts
 * @brief TanStack Query hooks for Company Admin dashboard KPIs.
 *
 * @details
 * Super Admin KPIs reuse `features/analytics/api` directly. Company
 * Admin metrics are scoped via the signed-in user's company and pull
 * from existing `/api/company/users`, `/api/sessions`, and
 * `/api/invoices` routes — no backend changes required.
 *
 * @dependencies
 * - shared/api/client, shared/api/endpoints
 * - features/dashboard/dateRanges
 *
 * @references
 * - BACKEND/src/features/company/routes.js
 * - BACKEND/src/features/sessions/routes.js
 * - BACKEND/src/features/invoices/routes.js
 *
 * @author DES
 * @date 07-06-2026
 */

import { resolveInvoiceTotals, isMixedCurrency } from "@/shared/utils/invoiceTotals";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { apiGet } from "@/shared/api/client";
import {
  companyEndpoints,
  invoiceEndpoints,
  sessionEndpoints,
} from "@/shared/api/endpoints";
import { monthStartIso, nowEndIso, last30DaysBaseRange } from "./dateRanges";
import { buildDailySessionTrend } from "./sessionTrend";
import { previousMonthToDate } from "./kpiCompare";

// ----------------------------------------------------------------------
// Schemas — lightweight parsing for dashboard-only responses
// ----------------------------------------------------------------------

const CompanyUsersResponseSchema = z.object({
  success: z.boolean().optional(),
  users: z
    .array(
      z
        .object({
          status: z.string().optional(),
        })
        .passthrough(),
    )
    .default([]),
});

const SessionsListResponseSchema = z.object({
  total: z.number().optional(),
  sessions: z
    .array(
      z
        .object({
          started_at: z.string().nullable().optional(),
          total_cents: z.number().nullable().optional(),
        })
        .passthrough(),
    )
    .optional()
    .default([]),
});

const InvoicesListResponseSchema = z.object({
  totalAmountCents: z.number().optional(),
  // (24-08-2026) Server-wide uncaptured total, per currency. See
  // shared/utils/invoiceTotals + docs/product/invoices.md §4b.
  openAmountCents: z.number().optional(),
  totalsByCurrency: z
    .array(
      z.object({
        currency: z.string(),
        amountCents: z.number(),
        openAmountCents: z.number().optional(),
        count: z.number().optional(),
      }),
    )
    .optional(),
  items: z
    .array(
      z.object({
        amount: z.number(),
        status: z.string(),
      }),
    )
    .optional(),
});

// ----------------------------------------------------------------------
// Query keys
// ----------------------------------------------------------------------

export const dashboardKeys = {
  company: {
    all: ["company", "dashboard"] as const,
    employees: () => ["company", "dashboard", "employees"] as const,
    sessionsMtd: () => ["company", "dashboard", "sessions-mtd"] as const,
    openBalance: () => ["company", "dashboard", "open-balance"] as const,
    sessionTrend: () => ["company", "dashboard", "session-trend"] as const,
    sessionsPrevMtd: () => ["company", "dashboard", "sessions-prev-mtd"] as const,
    spendPrevMtd: () => ["company", "dashboard", "spend-prev-mtd"] as const,
  },
};

// ----------------------------------------------------------------------
// Company Admin hooks
// ----------------------------------------------------------------------

export function useCompanyEmployeeStats() {
  return useQuery({
    queryKey: dashboardKeys.company.employees(),
    queryFn: async () => {
      const raw = await apiGet<unknown>(companyEndpoints.users);
      const parsed = CompanyUsersResponseSchema.parse(raw);
      const active = parsed.users.filter((u) => u.status === "active").length;
      return { total: parsed.users.length, active };
    },
  });
}

export function useCompanySessionsMtd() {
  return useQuery({
    queryKey: dashboardKeys.company.sessionsMtd(),
    queryFn: async () => {
      const usp = new URLSearchParams({
        from: monthStartIso(),
        to: nowEndIso(),
        limit: "1",
      });
      const raw = await apiGet<unknown>(`${sessionEndpoints.list}?${usp}`);
      const parsed = SessionsListResponseSchema.parse(raw);
      return parsed.total ?? 0;
    },
  });
}

/**
 * MTD open (uncaptured) balance for the signed-in company.
 *
 * Rewritten 24-08-2026. It used to fetch `pageSize: 100` and sum the open rows
 * of that ONE page — a company with more than 100 billable items this month got
 * a balance that had quietly stopped counting, with nothing on screen to say
 * so. It also mixed currencies into a single number, and it was the only query
 * in this file sending bare `YYYY-MM-DD` (which the backend reads as UTC
 * midnight) while its siblings send local-day ISO, so its window did not line
 * up with the cards beside it. Now: `pageSize: 1`, and the figure comes from
 * the server-side aggregate.
 */
export function useCompanyOpenBalance() {
  return useQuery({
    queryKey: dashboardKeys.company.openBalance(),
    queryFn: async () => {
      const usp = new URLSearchParams({
        from: monthStartIso(),
        to: nowEndIso(),
        pageSize: "1",
      });
      const raw = await apiGet<unknown>(`${invoiceEndpoints.list}?${usp}`);
      const parsed = InvoicesListResponseSchema.parse(raw);
      const totals = resolveInvoiceTotals(parsed);
      return {
        // Billed this month, the Invoices page's own total (03-10-2026: the
        // "Spend this month" card); the open part is shown only when > 0.
        amountCents: totals.amountCents,
        openAmountCents: totals.openAmountCents,
        currency: totals.currency,
        mixedCurrency: isMixedCurrency(totals),
      };
    },
  });
}

/**
 * The same days of last month, for the KPI deltas (03-10-2026): sessions
 * count and billed total, on the same routes as the month-to-date cards.
 */
export function useCompanySessionsPrevMtd() {
  return useQuery({
    queryKey: dashboardKeys.company.sessionsPrevMtd(),
    queryFn: async () => {
      const { from, to } = previousMonthToDate();
      const usp = new URLSearchParams({
        from: from.toISOString(),
        to: to.toISOString(),
        limit: "1",
      });
      const raw = await apiGet<unknown>(`${sessionEndpoints.list}?${usp}`);
      return SessionsListResponseSchema.parse(raw).total ?? 0;
    },
  });
}

export function useCompanySpendPrevMtd() {
  return useQuery({
    queryKey: dashboardKeys.company.spendPrevMtd(),
    queryFn: async () => {
      const { from, to } = previousMonthToDate();
      const usp = new URLSearchParams({
        from: from.toISOString(),
        to: to.toISOString(),
        pageSize: "1",
      });
      const raw = await apiGet<unknown>(`${invoiceEndpoints.list}?${usp}`);
      const totals = resolveInvoiceTotals(InvoicesListResponseSchema.parse(raw));
      return { amountCents: totals.amountCents, currency: totals.currency };
    },
  });
}

/** Daily session volume for the signed-in company (last 30 days). */
export function useCompanySessionTrend() {
  const range = last30DaysBaseRange();
  return useQuery({
    queryKey: dashboardKeys.company.sessionTrend(),
    queryFn: async () => {
      const usp = new URLSearchParams({
        from: range.startDate,
        to: range.endDate,
        limit: "500",
      });
      const raw = await apiGet<unknown>(`${sessionEndpoints.list}?${usp}`);
      const parsed = SessionsListResponseSchema.parse(raw);
      return buildDailySessionTrend(parsed.sessions, range.startDate, range.endDate);
    },
  });
}
